import { Alert, AlertTitle, Button } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

/**
 * Persistent "past due" warning banner (#2795), per the #2785 UX/UI design
 * spec, flow (e).
 *
 * Rendered once from `home-portal-page.tsx`, above the tab content — not
 * buried inside `PricingTab` — so the Owner sees it regardless of which tab
 * they're on, since access is never restricted while `past_due` (#2785
 * Story 4's "lenient, graceful" dunning policy: no hard/immediate
 * cancellation, full feature access retained during Stripe's retry
 * window).
 *
 * Deliberately uses MUI `Alert severity="warning"` (never `error`):
 * `error` reads as "something broken, our fault"; `warning` reads as
 * "action needed, but you're fine for now" — matching the confirmed
 * policy. Copy never threatens imminent cancellation or names a deadline
 * (exact dunning timing is a Stripe-managed detail this app must not
 * promise in its own words).
 *
 * Deliberately has no dismiss (X) control: per the UX spec, "dismissing a
 * real billing problem would undercut the clear call-to-action
 * requirement" — it stays visible for the entire duration of `past_due`
 * status, reappearing on every load/navigation regardless of a prior
 * dismiss, so the simplest correct behavior is to never offer one.
 *
 * `role="alert"` (via MUI's own `Alert`, which sets it by default) and the
 * theme's `warning` palette tokens (never hardcoded hex) satisfy the UX
 * spec's accessibility notes — screen readers announce the banner as soon
 * as it mounts, and the inline CTA button is a regular, keyboard-reachable
 * `<button>`.
 */
export interface PastDuePaymentBannerState {
  isVisible: boolean;
  /** Set once the Owner has successfully confirmed a replacement card but
   * the local subscription has not yet moved back to `active` (webhook
   * still pending) — see `UpdatePaymentMethodDialog`/`home-portal-plugin.ts`.
   * Swaps in reassuring "we'll retry shortly" copy instead of the initial
   * failure copy, so the Owner isn't left wondering whether anything
   * happened after they just fixed it. */
  isRetryPending?: boolean;
  language: SupportedLanguage;
}

export type PastDuePaymentBannerEvent = "open-update-payment-method-dialog";

export const PastDuePaymentBanner: PimoReactComponent<
  PastDuePaymentBannerState,
  PastDuePaymentBannerEvent,
  unknown
> = ({ fireEvent, isVisible, isRetryPending, language }) => {
  if (!isVisible) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      variant="standard"
      data-testid="past-due-payment-banner"
      sx={{ mb: 2, alignItems: "center" }}
      action={
        isRetryPending ? undefined : (
          <Button
            color="inherit"
            size="small"
            onClick={() => fireEvent?.("open-update-payment-method-dialog")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {getLocalizedMessage(
              language,
              "pastDuePaymentBanner.updatePaymentMethod"
            )}
          </Button>
        )
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {getLocalizedMessage(language, "pastDuePaymentBanner.headline")}
      </AlertTitle>
      {isRetryPending
        ? getLocalizedMessage(language, "pastDuePaymentBanner.retryPending")
        : getLocalizedMessage(language, "pastDuePaymentBanner.body")}
    </Alert>
  );
};
